// useUploadExtractedText - fetch the kept body text for one upload.
//
// Backs the "View extraction" button on the project Files page. The text the
// extractor read is persisted off the Postgres hot path (it can be large), so
// it's fetched lazily - the button only enables the hook once the operator
// opens the modal, exactly like the AI-trace viewer.
import { useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

export type ExtractionStopReason =
  | "complete"
  | "appendix"
  | "page_budget"
  | "char_budget"
  | "ocr"
  // A PDF with no machine-readable text layer AND no renderable page, or a
  // DOCX with no extractable text.
  | "unreadable";

export interface ExtractedTextView {
  // The bounded report body the extractor read. Empty string on the OCR path
  // (the model read page images - there is no selectable source text).
  text: string;
  // Where the parse stopped, so the viewer can flag when there's more in the
  // file than was read.
  stop_reason: ExtractionStopReason;
  total_pages: number | null;
  pages_parsed: number | null;
  char_count: number;
  // True when the stored body was longer than this panel's cap and the BE clipped it
  // for display only. The extraction itself is complete - distinct from a
  // ``stop_reason`` that says the PARSE stopped early - and the agent reads the whole
  // body regardless.
  display_truncated?: boolean;
  // > 0 when the survey was extracted via OCR over rendered page images.
  ocr_image_count: number;
}

interface ExtractedTextResponse {
  extraction: ExtractedTextView | null;
}

const QUERY_KEY = QK.uploadExtractedText;

export const useUploadExtractedText = (
  upload_id: string | null | undefined,
) => {
  const http = useAgentsHttp();
  return useQuery<ExtractedTextView | null>(
    [QUERY_KEY, upload_id ?? null],
    async () => {
      if (!upload_id) {
        throw new Error("upload_id is required");
      }
      const resp = await http<ExtractedTextResponse>("/uploads/extracted-text", {
        upload_id,
      });
      return resp.extraction;
    },
    {
      enabled: Boolean(upload_id),
      refetchOnWindowFocus: false,
      // The kept text changes only when the file is replaced / re-extracted,
      // neither frequent - cache for the session.
      staleTime: Infinity,
    },
  );
};
